import { DeleteUserDialog } from "@/components/admin/DeleteUserDialog";
import { GrantCell } from "@/components/admin/GrantCell";
import { fmtUSD } from "@/lib/money";

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground ${right ? "text-right" : ""}`}>
    {children}
  </th>
);

const fmtJoined = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const isThisWeek = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && Date.now() - t < 7 * 24 * 60 * 60 * 1000;
};

const UserRow = ({ u, currentUserId, onGranted, onDeleted }) => (
  <tr className="border-b border-border last:border-0 hover:bg-[#0F0F10]" data-testid={`admin-user-row-${u.id}`}>
    <td className="px-4 py-3">
      <p className="font-medium">
        {u.name || "—"}
        {u.id === currentUserId && <span className="ml-2 text-[10px] text-[#007AFF] uppercase tracking-widest">you</span>}
        {isThisWeek(u.created_at) && (
          <span className="ml-2 text-[10px] text-emerald-400 uppercase tracking-widest" data-testid={`admin-new-badge-${u.id}`}>new</span>
        )}
      </p>
      <p className="text-xs text-muted-foreground font-mono">{u.email}</p>
    </td>
    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.phone_number || "—"}</td>
    <td className="px-4 py-3">
      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${
        u.role === "admin" ? "border-[#007AFF]/40 text-[#007AFF]" : "border-border text-muted-foreground"}`}>
        {u.role}
      </span>
    </td>
    <td className="px-4 py-3 text-xs text-muted-foreground font-mono whitespace-nowrap" data-testid={`admin-joined-${u.id}`}>
      {fmtJoined(u.created_at)}
    </td>
    <td className="px-4 py-3 text-right font-mono font-bold" data-testid={`admin-balance-${u.id}`}>{fmtUSD(u.credits)}</td>
    <td className="px-4 py-3"><GrantCell userId={u.id} onGranted={onGranted} /></td>
    <td className="px-2 py-3 text-right">
      {u.id !== currentUserId && <DeleteUserDialog user={u} onDeleted={onDeleted} />}
    </td>
  </tr>
);

export const UsersTable = ({ users, loading, currentUserId, onGranted, onDeleted }) => {
  const newThisWeek = users.filter((u) => isThisWeek(u.created_at)).length;
  return (
    <div className="border border-border bg-[#0A0A0A] rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="font-heading text-lg font-black tracking-tight">Users</h2>
        {!loading && (
          <p className="text-xs font-mono text-muted-foreground" data-testid="admin-users-stat">
            <span className="text-white font-bold">{users.length}</span> users
            {newThisWeek > 0 && (
              <> · <span className="text-emerald-400 font-bold">{newThisWeek}</span> new this week</>
            )}
          </p>
        )}
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground font-mono">Loading…</p>
        ) : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-border text-left">
                <Th>User</Th>
                <Th>Phone</Th>
                <Th>Role</Th>
                <Th>Joined</Th>
                <Th right>Balance</Th>
                <Th right>Grant credits</Th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow key={u.id} u={u} currentUserId={currentUserId} onGranted={onGranted} onDeleted={onDeleted} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
